import { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import { decodeJsonBase64, encodeJsonBase64 } from './planStringTransfer'

export type RaidPlanTransferPayload = {
  timeline: RaidPlanTimeline
}

export type RaidPlanImportSuccess = {
  ok: true
  timeline: RaidPlanTimeline
  actorCount: number
  castCount: number
  assignmentCount: number
}

export type RaidPlanImportFailure = {
  ok: false
  error: 'invalid'
}

export type RaidPlanImportResult = RaidPlanImportSuccess | RaidPlanImportFailure

const countCasts = (timeline: RaidPlanTimeline): number => {
  const actorCasts = timeline.actors.reduce(
    (total, actor) => total + (actor.casts?.length ?? 0),
    0,
  )
  const generalCasts = timeline.general?.casts?.length ?? 0
  return actorCasts + generalCasts
}

const countAssignments = (timeline: RaidPlanTimeline): number =>
  Object.keys(timeline.encounter_assignments?.abilities ?? {}).length

export const countRaidPlanContent = (timeline: RaidPlanTimeline): number =>
  timeline.actors.length + countCasts(timeline) + countAssignments(timeline)

const isRaidPlanTimeline = (value: unknown): value is RaidPlanTimeline => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const timeline = value as Record<string, unknown>
  return Array.isArray(timeline.actors)
}

export const encodeRaidPlanTimeline = (timeline: RaidPlanTimeline): string => {
  const payload: RaidPlanTransferPayload = { timeline }
  return encodeJsonBase64(payload)
}

export const parseRaidPlanImport = (input: string): RaidPlanImportResult => {
  const trimmed = input.trim()
  if (trimmed === '') {
    return { ok: false, error: 'invalid' }
  }

  try {
    const parsed = decodeJsonBase64(trimmed)

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return { ok: false, error: 'invalid' }
    }

    const timeline = (parsed as Record<string, unknown>).timeline
    if (!isRaidPlanTimeline(timeline)) {
      return { ok: false, error: 'invalid' }
    }

    return {
      ok: true,
      timeline,
      actorCount: timeline.actors.length,
      castCount: countCasts(timeline),
      assignmentCount: countAssignments(timeline),
    }
  } catch {
    return { ok: false, error: 'invalid' }
  }
}
